import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePathLang } from '../lib/i18n';
import {
  getConsent, acceptAllConsent, acceptNecessaryConsent, ensureAnalytics, onConsentChange,
} from '../lib/consent';

// Cookie banner: compact bar at the bottom, LVG colors (#d5ab63 gold).
// Shown until a choice is stored; "Setări cookie" re-opens it.
const CookieBanner = () => {
  const lang = usePathLang();
  const [visible, setVisible] = useState(false);
  const [hasChoice, setHasChoice] = useState(false);

  useEffect(() => {
    // client-only: never rendered in SSR
    setHasChoice(Boolean(getConsent()));
    if (!getConsent()) setVisible(true);
    const offOpen = () => setVisible(true);
    const offChange = () => setHasChoice(Boolean(getConsent()));
    window.addEventListener('lvg-open-settings', offOpen);
    window.addEventListener('lvg-consent-changed', offChange);
    return () => {
      window.removeEventListener('lvg-open-settings', offOpen);
      window.removeEventListener('lvg-consent-changed', offChange);
    };
  }, []);

  useEffect(() => {
    if (!hasChoice) return;
    const cleanup = onConsentChange(() => {
      ensureAnalytics();
    });
    ensureAnalytics();
    return cleanup;
  }, [hasChoice]);

  if (!visible) return null;

  const choose = (which) => {
    const state = which === 'all' ? acceptAllConsent() : acceptNecessaryConsent();
    setVisible(false);
    setHasChoice(true);
    if (state.analytics) ensureAnalytics();
  };

  const t = (ro, ru) => (lang === 'ru' ? ru : ro);
  const cookiePath = lang === 'ru' ? '/ru/politika-cookie' : '/politica-cookie';

  return (
    <div
      role="dialog"
      aria-label={t('Banner cookie', 'Баннер cookie')}
      className="fixed bottom-0 left-0 right-0 z-[400] bg-white border-t-[1px] border-[#d5ab63] shadow-[0_-4px_20px_rgba(0,0,0,0.12)]"
    >
      <div className="sm:container sm:mx-auto px-[16px] py-[14px] flex flex-col sm:flex-row sm:items-center gap-[12px] justify-between">
        <div className="flex flex-col gap-[4px] max-w-[720px]">
          <p className="font-poppins text-[15px] font-[500] text-[#181616]">
            {t('Folosim cookie-uri', 'Мы используем cookie')}
          </p>
          <p className="font-body text-[13.5px] text-gray-600 leading-[1.5]">
            {t(
              'Utilizăm cookie-uri necesare și, cu acordul dvs., cookie-uri pentru analiză, publicitate și servicii externe.',
              'Мы используем необходимые cookie, а с вашего согласия — cookie для аналитики, рекламы и внешних сервисов.'
            )}{' '}
            <Link to={cookiePath} className="text-[#8a5f2b] underline hover:text-[#b48a4d]">
              {t('Detalii', 'Подробнее')}
            </Link>
          </p>
        </div>
        <div className="flex gap-[10px] shrink-0">
          <button
            type="button"
            onClick={() => choose('necessary')}
            className="font-body text-[13.5px] px-[18px] py-[9px] rounded-[5px] border-[1px] border-gray-300 text-[#181616] hover:bg-gray-100 cursor-pointer transition-all duration-100 ease-in"
          >
            {t('Doar necesare', 'Только необходимые')}
          </button>
          <button
            type="button"
            onClick={() => choose('all')}
            className="btn-main text-white font-body text-[13.5px] font-[500] px-[22px] py-[9px] rounded-[5px] cursor-pointer"
          >
            {t('Accept', 'Принять')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
